/*
 * NFT Landing Page - Sailing Ships Collection
 * Copyright (C) 2025 jsaif
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 *
 * Original project: https://github.com/s-shemmee/NFT-Portfolio-Landing-Page
 * Original creator: s-shemmee
 */

import React, { useState } from "react";
import "./Contact.css";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState({
    submitted: false,
    submitting: false,
    error: null,
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = "Name is required";
    } else if (form.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!form.subject.trim()) {
      newErrors.subject = "Subject is required";
    } else if (form.subject.trim().length < 3) {
      newErrors.subject = "Subject must be at least 3 characters";
    }

    if (!form.message.trim()) {
      newErrors.message = "Message is required";
    } else if (form.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      email: "",
      subject: "",
      message: "",
    });
    setErrors({});
    setStatus({ submitted: false, submitting: false, error: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setStatus({ submitted: false, submitting: true, error: null });

    try {
      // Formspree submission
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("subject", form.subject);
      formData.append("message", form.message);

      // Replace 'xyzjkqpl' with your actual Formspree form ID
      const response = await fetch("https://formspree.io/f/xyzjkqpl", {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        setStatus({ submitted: true, submitting: false, error: null });

        // Reset form after successful submission
        setTimeout(() => {
          resetForm();
        }, 5000);
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      setStatus({
        submitted: false,
        submitting: false,
        error: "Failed to send message. Please try again or contact us directly.",
      });
    }
  };

  return (
    <section className="contact-section" id="contact-section">
      <div className="container">
        <h1 className="contact-title">
          <span>Contact Us</span>
        </h1>
        <p className="contact-desc">
          Have a question or want to get in touch? Fill out the form below and our team will get back to you soon.
        </p>

        {status.submitted ? (
          <div className="contact-success">
            <div className="success-icon">✓</div>
            <h2>Thank you!</h2>
            <p>Your message has been sent successfully. We'll be in touch soon.</p>
            <button className="contact-btn secondary" onClick={resetForm} type="button">
              Send Another Message
            </button>
          </div>
        ) : (
          <form
            className="contact-form"
            onSubmit={handleSubmit}
            autoComplete="off"
            action="https://formspree.io/f/xyzjkqpl"
            method="POST"
          >
            {status.error && (
              <div className="contact-error">
                <div className="error-icon">⚠</div>
                <p>{status.error}</p>
                <button
                  className="contact-btn secondary small"
                  onClick={() => setStatus({ ...status, error: null })}
                  type="button"
                >
                  Dismiss
                </button>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  onKeyDown={(e) => e.stopPropagation()}
                  onKeyPress={(e) => e.stopPropagation()}
                  required
                  autoComplete="off"
                  className={errors.name ? "error" : ""}
                  disabled={status.submitting}
                />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  onKeyDown={(e) => e.stopPropagation()}
                  onKeyPress={(e) => e.stopPropagation()}
                  required
                  autoComplete="off"
                  className={errors.email ? "error" : ""}
                  disabled={status.submitting}
                />
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="subject">Subject *</label>
              <input
                id="subject"
                name="subject"
                type="text"
                value={form.subject}
                onChange={handleChange}
                onKeyDown={(e) => e.stopPropagation()}
                onKeyPress={(e) => e.stopPropagation()}
                required
                autoComplete="off"
                className={errors.subject ? "error" : ""}
                disabled={status.submitting}
              />
              {errors.subject && <span className="field-error">{errors.subject}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                rows={5}
                value={form.message}
                onChange={handleChange}
                onKeyDown={(e) => {
                  // Prevent event bubbling for space bar and other keys
                  e.stopPropagation();
                }}
                onKeyPress={(e) => {
                  // Ensure space bar works in textarea
                  e.stopPropagation();
                }}
                required
                className={errors.message ? "error" : ""}
                disabled={status.submitting}
                placeholder="Tell us about your inquiry..."
              />
              {errors.message && <span className="field-error">{errors.message}</span>}
            </div>

            <div className="form-actions">
              <button className="contact-btn" type="submit" disabled={status.submitting}>
                {status.submitting ? (
                  <>
                    <span className="spinner"></span> Sending...
                  </>
                ) : (
                  "Send Message"
                )}
              </button>

              <button className="contact-btn secondary" type="button" onClick={resetForm} disabled={status.submitting}>
                Reset Form
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
